import { useState } from "react";
import { useBannerContext } from "oolib";
import { useUpdatePendingDirectDataImport } from "./useUpdatePendingDirectDataImport";
import { useDeletePendingDirectDataImport } from "./useDeletePendingDirectDataImport";

/**
 * Owns the "save import for later" (pending import / draft) lifecycle for a
 * single DirectDataImport session, so the (already large) DirectDataImport
 * component doesn't carry this cross-cutting flow inline.
 *
 * Responsibilities:
 *  - saveDraft            → name-modal for a new draft, or a silent re-save
 *                           when resuming an already-named one.
 *  - deleteOnDiscard      → delete the draft when the user discards the session.
 *
 * NOTE: cleanup on a *successful import* is NOT done here — the BE deletes the
 * draft atomically inside the first batch's tpl-creation transaction (driven by
 * the pendingDirectDataImportId the FE passes into the import request), so a
 * failed delete rolls back with the import. Doing it on the FE could only fire at
 * enqueue-time, which would drop the draft even if the queued import later failed.
 *
 * `onSessionExit` runs after a draft is (re)saved — the component uses it to
 * reset its local UI state and close.
 */
export const usePendingDirectDataImportSession = ({ resumePendingDirectDataImport, onSessionExit }) => {
  const { SET_ALERT_BANNER } = useBannerContext();
  // Resuming a named draft only ever updates it in place (create happens via the
  // name modal / useCreatePendingDirectDataImport).
  const { mutate: updatePendingDirectDataImport, isLoading: isSaving } = useUpdatePendingDirectDataImport();
  const { mutate: deletePendingDirectDataImport } = useDeletePendingDirectDataImport();

  // The draft this session is tied to. Set when resumed; cleared the moment the
  // draft is consumed (imported / re-saved / discarded) so a later action in
  // the same mount can't act on a draft that's already gone.
  const [pendingDirectDataImportId, setPendingDirectDataImportId] = useState(
    resumePendingDirectDataImport?._id || null
  );
  const [showSaveModal, setShowSaveModal] = useState(false);

  // A resumed draft already has a name — re-saving updates it silently rather
  // than re-prompting via the name modal.
  const isResumedNamedDraft = !!(pendingDirectDataImportId && resumePendingDirectDataImport?.previewName);

  // Discarding a resumed draft throws away exactly the work it held.
  const deleteOnDiscard = () => {
    if (!pendingDirectDataImportId) return;
    deletePendingDirectDataImport(pendingDirectDataImportId);
    setPendingDirectDataImportId(null);
  };

  const resaveDraft = ({ schemaConfig, contentType }) => {
    updatePendingDirectDataImport(
      {
        pendingDirectDataImportId,
        previewName: resumePendingDirectDataImport.previewName,
        schemaExtractorOutline: schemaConfig?.schemaExtractor?.outline,
        fileNameInGcs: schemaConfig?.fileNameInGcs,
        kp_content_type: contentType,
        metadata: schemaConfig?.metadata,
      },
      {
        onSuccess: () => {
          SET_ALERT_BANNER("Draft saved successfully!", "green", 3000);
          setPendingDirectDataImportId(null);
          onSessionExit?.();
        },
        onError: (error) =>
          SET_ALERT_BANNER(
            error?.response?.data?.errors?.[0]?.msg ||
              error?.response?.data?.error ||
              "Failed to save draft",
            "red",
            3000
          ),
      }
    );
  };

  // "Save as Draft" entry point: silent re-save for a resumed named draft,
  // else open the name modal for a brand-new one.
  const saveDraft = ({ schemaConfig, contentType }) => {
    if (isResumedNamedDraft) resaveDraft({ schemaConfig, contentType });
    else setShowSaveModal(true);
  };

  return {
    showSaveModal,
    setShowSaveModal,
    isSaving,
    saveDraft,
    deleteOnDiscard,
  };
};
